import { Box } from '@mui/material';
import {
  Button,
  FullNameError,
  Modal,
  ModalProps,
  TextInput,
} from 'components/ui';
import { UpdateNameForm, updateNameFormSchema } from 'forms';
import { useAppForm, useProfile } from 'hooks';
import React, { ReactElement, useEffect, useState } from 'react';
import { useProfileApi } from 'services/api';
import { throttle } from 'lodash';
import { useSnackbar } from 'notistack';

export interface UpdateNameModalProps
  extends Pick<ModalProps, 'open' | 'onClose'> {}

export const UpdateNameModal = ({
  open,
  onClose,
}: UpdateNameModalProps): ReactElement => {
  const { profile } = useProfile({ revalidateOnMount: true });

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, touchedFields: touched },
  } = useAppForm(updateNameFormSchema);

  const [isSubmitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) setSubmitting(false);

    if (!profile) return;

    reset({
      firstName: profile.firstName,
      lastName: profile.lastName,
    });
  }, [profile, open]);

  const profileApi = useProfileApi();

  const { enqueueSnackbar } = useSnackbar();

  const onSubmit = throttle(
    async (data: UpdateNameForm) => {
      setSubmitting(true);
      const result = await profileApi.setName(data);

      if (result.success) return onClose?.();

      setSubmitting(false);
      enqueueSnackbar(result.error.message, { variant: 'error' });
    },
    200,
    { leading: true },
  );

  return (
    <Modal title="Update Your Name" open={open} onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ width: 600, maxWidth: '80vw' }} display="flex">
          <Box flex={1} mr={20}>
            <TextInput
              label="First Name"
              defaultValue={profile?.firstName}
              hasError={touched.firstName && !!errors.firstName}
              {...register('firstName')}
            />
          </Box>
          <Box flex={1}>
            <TextInput
              label="Last Name"
              defaultValue={profile?.lastName}
              hasError={touched.lastName && !!errors.lastName}
              {...register('lastName')}
            />
          </Box>
        </Box>

        {profile && <FullNameError touched={touched} control={control} />}

        <Box mt={30} display="flex">
          <Box mr={30}>
            <Button minWidth={100} inverted onClick={onClose}>
              Cancel
            </Button>
          </Box>
          <Button
            minWidth={100}
            type="submit"
            variant="vibrant"
            disabled={isSubmitting}
          >
            Save
          </Button>
        </Box>
      </form>
    </Modal>
  );
};
